import type { AuthGateway } from '@forq/contracts';
import type { ServiceBillingGateway } from '@forq/service-billing-data';
import {
  createForqSupabaseClient,
  isSupabaseConfigured,
  SupabaseAuthGateway,
  SupabaseOperationError,
  PreviewSupabaseServiceBillingGateway,
  type ForqSupabaseConfig,
} from '@forq/supabase';

export interface PreviewPersistenceEnvironment {
  readonly VITE_FORQ_PREVIEW_SUPABASE_URL?: string;
  readonly VITE_FORQ_PREVIEW_SUPABASE_ANON_KEY?: string;
  /** Die Namen bis zum 08.09.2026. Werden weiter gelesen, siehe unten. */
  readonly VITE_FORQ_NEXT_PREVIEW_SUPABASE_URL?: string;
  readonly VITE_FORQ_NEXT_PREVIEW_SUPABASE_ANON_KEY?: string;
}

export type PreviewPersistenceConfiguration =
  | { readonly kind: 'disabled' }
  | { readonly kind: 'configuration-error'; readonly message: string }
  | { readonly kind: 'supabase-preview'; readonly config: ForqSupabaseConfig };

export interface PreviewPersistenceServices {
  readonly auth: AuthGateway;
  readonly billing: ServiceBillingGateway;
  readonly ensureAccess: () => Promise<void>;
}

let cachedServices: { readonly key: string; readonly services: PreviewPersistenceServices } | null = null;

export function resolvePreviewPersistenceConfiguration(
  environment: PreviewPersistenceEnvironment,
): PreviewPersistenceConfiguration {
  // Die Variablen heissen seit dem 08.09.2026 VITE_FORQ_PREVIEW_*. Die alten Namen werden
  // weiter gelesen: sie stehen in Cloudflare, und ein Bau, der sie ignoriert, wuerde die
  // Vorschau-Datenbank stillschweigend abschalten -- die Anwendung liefe dann im Demo-Modus
  // weiter, ohne dass jemand einen Fehler saehe. Sind die Namen in Cloudflare umgestellt,
  // koennen die beiden Rueckfaelle hier weg.
  // Ein LEERER neuer Wert zaehlt dabei als nicht gesetzt: wer die Variable in Cloudflare
  // anlegt und den Wert erst spaeter eintraegt, soll nicht auf halbem Weg ohne Vorschau
  // dastehen -- `??` allein wuerde die leere Zeichenkette nehmen und den alten Namen uebergehen.
  const waehle = (neu?: string, alt?: string): string => neu?.trim() || alt?.trim() || '';
  const url = waehle(environment.VITE_FORQ_PREVIEW_SUPABASE_URL, environment.VITE_FORQ_NEXT_PREVIEW_SUPABASE_URL);
  const anonKey = waehle(environment.VITE_FORQ_PREVIEW_SUPABASE_ANON_KEY, environment.VITE_FORQ_NEXT_PREVIEW_SUPABASE_ANON_KEY);
  if (!url && !anonKey) return { kind: 'disabled' };
  if (!url || !anonKey) {
    return {
      kind: 'configuration-error',
      message: 'Die FORQ-Preview-Datenbank ist nur teilweise konfiguriert. URL und Anon-Key müssen gemeinsam gesetzt sein.',
    };
  }
  const config = { url, anonKey };
  if (!isSupabaseConfigured(config)) {
    return { kind: 'configuration-error', message: 'Die Preview-Supabase-Konfiguration ist unvollständig.' };
  }
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('Unsupported protocol.');
  } catch {
    return { kind: 'configuration-error', message: 'Die Preview-Supabase-URL ist ungültig.' };
  }
  return { kind: 'supabase-preview', config };
}

export function createPreviewPersistenceServices(
  config: ForqSupabaseConfig,
): PreviewPersistenceServices {
  const key = `${config.url.trim()}\u0000${config.anonKey.trim()}`;
  if (cachedServices?.key === key) return cachedServices.services;
  const client = createForqSupabaseClient(config);
  const services: PreviewPersistenceServices = {
    auth: new SupabaseAuthGateway(client),
    billing: new PreviewSupabaseServiceBillingGateway(client),
    ensureAccess: async () => {
      const { data, error } = await client.schema('forq_next_preview').rpc('claim_initial_access');
      if (error) throw new SupabaseOperationError('preview.access', error.message);
      if (!data || typeof data !== 'object') {
        throw new SupabaseOperationError('preview.access', 'Die Preview-Zugriffsprüfung lieferte kein gültiges Ergebnis.');
      }
    },
  };
  cachedServices = { key, services };
  return services;
}
